import { useRef, useState } from "preact/hooks";
import { ACCENTS, accentReadable, ringColor } from "../accents.js";
import { useObjectUrls } from "../chrome/object-urls.js";
import { validateLogo } from "../logo-file.js";
import { COVER_ID, DEFAULT_EVENT_NAME, seedSrc } from "../seed.js";
import type { AccentId, DemoBrand, StoredImage } from "../types.js";

export interface BrandScreenProps {
  readonly initial: DemoBrand | null;
  onCreate(brand: DemoBrand): void;
}

export function BrandScreen({ initial, onCreate }: BrandScreenProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [accent, setAccent] = useState<AccentId>(initial?.accent ?? "green");
  const [logo, setLogo] = useState<StoredImage | null>(initial?.logo ?? null);
  const [eventName, setEventName] = useState(initial?.eventName ?? DEFAULT_EVENT_NAME);
  const [logoError, setLogoError] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const logoUrl = useObjectUrls(logo ? [{ id: "logo", image: logo }] : []).get("logo");
  const trimmedName = name.trim();
  const trimmedEvent = eventName.trim();
  const valid = trimmedName.length > 0 && trimmedEvent.length > 0;

  const pick = async (file: File | undefined) => {
    if (!file) {
      return;
    }
    const error = validateLogo(file);
    setLogoError(error);
    if (!error) {
      setLogo({ bytes: await file.arrayBuffer(), type: file.type });
    }
  };

  return (
    <div class="d-screen d-scroll" data-theme="light" data-testid="screen-brand">
      <main class="d-brand">
        <p class="d-micro">Demonstração</p>
        <h1 class="d-title">Monte um evento de exemplo com a sua marca</h1>
        <div class="d-brand-preview" role="img" aria-label="Prévia do convite">
          <img src={seedSrc(COVER_ID, "s")} alt="" />
          <div class="d-brand-preview-body">
            <div class="d-invite-brand" style={{ borderBottomColor: ringColor(accent) }}>
              {logoUrl ? <img class="d-host-logo" src={logoUrl} alt="" /> : null}
              <span class="d-invite-host">Convidado por {trimmedName || "seu cerimonial"}</span>
            </div>
            <p class="d-brand-preview-headline">
              As fotos do evento, <em>por você</em>
            </p>
            <p class="d-brand-preview-card">{trimmedEvent || DEFAULT_EVENT_NAME}</p>
          </div>
        </div>
        <form
          class="d-brand-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid) {
              onCreate({ name: trimmedName, accent, logo, eventName: trimmedEvent });
            }
          }}
        >
          <label class="d-field">
            <span>Nome do cerimonial</span>
            <input
              class="d-input"
              value={name}
              maxLength={40}
              placeholder="Ateliê Marília"
              onInput={(event) => setName((event.currentTarget as HTMLInputElement).value)}
            />
          </label>
          <fieldset class="d-field">
            <legend>Cor de destaque</legend>
            <div class="d-swatches">
              {ACCENTS.map((option) => (
                <div key={option.id} class="d-swatch-item">
                  <button
                    type="button"
                    class="d-swatch"
                    aria-label={option.label}
                    aria-pressed={option.id === accent}
                    style={{ background: option.hex }}
                    onClick={() => setAccent(option.id)}
                  />
                  <span aria-hidden="true">{option.label}</span>
                </div>
              ))}
            </div>
            <p class="d-caption">
              A cor entra só no anel e na marca. Botões, links e fundos continuam neutros: a cor da
              tela vem das fotos.
            </p>
            {accentReadable(accent) ? null : <p class="d-warning">Essa cor some sobre a foto</p>}
          </fieldset>
          <div class="d-field">
            <label for="demo-logo">Logotipo (opcional)</label>
            <div class="d-logo-row">
              {logoUrl ? (
                <img class="d-logo-preview" src={logoUrl} alt="Logotipo escolhido" />
              ) : null}
              <button
                type="button"
                class="d-button d-secondary d-small"
                onClick={() => picker.current?.click()}
              >
                Escolher arquivo
              </button>
              <input
                ref={picker}
                id="demo-logo"
                class="d-hidden-input"
                tabIndex={-1}
                type="file"
                accept="image/png,image/jpeg,.png,.jpg,.jpeg"
                onChange={(event) =>
                  void pick((event.currentTarget as HTMLInputElement).files?.[0])
                }
              />
              {logo ? (
                <button type="button" class="d-link" onClick={() => setLogo(null)}>
                  Remover logo
                </button>
              ) : null}
            </div>
            <p class="d-caption">PNG ou JPEG, até 2 MB.</p>
            {logoError ? <p class="d-error">{logoError}</p> : null}
          </div>
          <label class="d-field">
            <span>Nome do evento</span>
            <input
              class="d-input"
              value={eventName}
              maxLength={60}
              onInput={(event) => setEventName((event.currentTarget as HTMLInputElement).value)}
            />
          </label>
          <button
            type="submit"
            class="d-button d-primary d-large"
            disabled={!valid}
            data-guide-target="brand"
          >
            Criar evento
          </button>
          {valid ? null : (
            <p class="d-caption d-brand-missing">
              {trimmedName.length === 0
                ? "Digite o nome do cerimonial para continuar."
                : "Digite o nome do evento para continuar."}
            </p>
          )}
          <p class="d-caption">Tudo fica só neste navegador. Nada é enviado.</p>
        </form>
      </main>
    </div>
  );
}
